import Button from "@/components/bricks/button/Button";
import CloseIcon from "../../../../icons/CloseIcon";
import EditIcon from "../../../../icons/EditIcon";
import {
  parseDurationToDate,
  stringDateToPPP,
  stringISODateToTime,
  // stringDateToyyyyMMdd,
} from "../../../../../util/date";
// import { defaultIfNullish } from "../../../../../util/stringUtil";
//import AddressPicker from "../../../../formcontrols/AddressPicker";
//import InputRadio from "../../../../formcontrols/InputRadio";
import { TextInputFormik } from "../../../../formcontrols/InputItem";
import { FormikTextArea } from "../../../../formcontrols/TextArea";
import { FormikDatePicker } from "../../../../formcontrols/DatePicker";
import { DisplayItem } from "../../../../formcontrols/DisplayItem";
import { useKursusoplysningerFormik } from "./formik/useKursusoplysninger";
import {
  ApolloQueryResult,
  OperationVariables,
} from "@apollo/client/core/types";
import AutoCompleteFormControl from "../../../../formcontrols/AutoCompleteFormControl";
import {
  getPatchOperations,
  handleFormikChange,
} from "../../../../../util/form";
import { useHotels } from "../../../../../hooks/useQueries";
import StamopIcon from "../../../../icons/StamopIcon";
import { Kursushold } from "../../../../../interfaces/schema";
import { defaultIfNullish } from "../../../../../util/stringUtil";
import FormikTimePicker from "../../../../formcontrols/TimePicker";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { useMutation } from "@tanstack/react-query";
import { patch } from "../../../../../api/util";

const Kursusoplysninger = ({
  editMode,
  setEditMode,
  kursushold,
  invalidateData,
}: {
  editMode: boolean;
  setEditMode: (editmode: boolean) => void;
  kursushold: Kursushold;
  invalidateData: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<
    ApolloQueryResult<{
      kursushold: Kursushold;
    }>
  >;
}) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "KursusWrite");
  const {
    isPending: mutationIsLoading,
    mutate,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<unknown, { status: number }, Record<string, any>, unknown>({
    onSuccess: async function () {
      await invalidateData();
      setEditMode(false);
    },
    onError: () => {
      setTimeout(() => {
        resetMutation();
      }, 3000);
    },
    mutationFn: (values: Record<string, any>) => {
      return patch({
        query: `kursus/${kursushold.id}`,
        body: JSON.stringify(getPatchOperations(values)),
      });
    },
  });

  const formik = useKursusoplysningerFormik(
    {
      ...kursushold,
      fraKlDag: kursushold.fraKlDag
        ? parseDurationToDate(kursushold.fraKlDag)
        : kursushold.fraKlDag,
      tilKlDag: kursushold.tilKlDag
        ? parseDurationToDate(kursushold.tilKlDag)
        : kursushold.tilKlDag,
    } as any,
    mutate,
  );
  const { handleSubmit, initialValues, values, dirty } = formik;

  const { getHotelById, hotels, getHotelsError, getHotelsLoading } =
    useHotels();

  const hotel = getHotelById(values.hotelId ?? "");
  const kursusTypeIsTemaOrFMUProeve =
    kursushold.kursustype === 1 || kursushold.kursustype === 5;
  const kursusTypeIsFMUProeve = kursushold.kursustype === 5;
  return (
    <div className={`c-card c-card--editable${editMode ? " is-editable" : ""}`}>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
        <h3 className="d-flex align-items-center gap-2">
          <span className="c-theme-icon bg-primary-light text-primary">
            <StamopIcon />
          </span>{" "}
          Stamoplysninger
        </h3>
        {canWrite && (
          <Button
            type="button"
            transparent
            color="primary"
            onClick={() => {
              if (editMode) formik.resetForm();
              setEditMode(!editMode);
            }}
            className="edit-trigger"
          >
            {editMode && (
              <>
                Luk <CloseIcon />
              </>
            )}
            {!editMode && (
              <>
                Redigér
                <EditIcon />
              </>
            )}
          </Button>
        )}
      </div>
      <div className="row g-4">
        <div className="col-12 col-xl-6 d-flex flex-column gap-4">
          <div className="c-item-list c-card">
            <h4 className="flex-100 mt-n1">Kursusoplysninger</h4>
            {editMode && (
              <div>
                <TextInputFormik
                  formik={formik}
                  config={{
                    component: "textinput",
                    id: "navn",
                    initialValue: initialValues.navn,
                    label: "Kursusnavn",
                  }}
                />
              </div>
            )}
            {!editMode && (
              <div>
                <DisplayItem
                  label="Kursusnavn"
                  text={kursushold.navn}
                  isEditable={true}
                />
              </div>
            )}
            <div>
              <DisplayItem
                label="Kategori"
                text={kursushold.fmukursusKategoriNavigation?.navn ?? "-"}
              />
            </div>
            <div>
              {editMode && (
                <>
                  <FormikDatePicker
                    config={{
                      component: "datepicker",
                      id: "fraDato",
                      label: "Startdato",
                      initialValue: kursushold.fraDato ?? undefined,
                    }}
                    formik={formik}
                  />
                </>
              )}
              {!editMode && (
                <DisplayItem
                  label="Startdato"
                  text={stringDateToPPP(kursushold.fraDato ?? "")}
                  isEditable={true}
                />
              )}
            </div>
            {editMode && (
              <div>
                <FormikTimePicker
                  config={{
                    component: "timepicker",
                    id: "fraKlDag",
                    label: "Starttidspunkt",
                  }}
                  formik={formik}
                />
              </div>
            )}
            {!editMode && (
              <div>
                <DisplayItem
                  label="Starttidspunkt"
                  text={stringISODateToTime(
                    kursushold.fraDato ?? "",
                    kursushold.fraKlDag ?? "",
                  )}
                  isEditable={true}
                />
              </div>
            )}
            <div>
              {editMode && (
                <>
                  <FormikDatePicker
                    config={{
                      component: "datepicker",
                      id: "tilDato",
                      label: "Slutdato",
                      initialValue: kursushold.tilDato ?? undefined,
                    }}
                    formik={formik}
                  />
                </>
              )}
              {!editMode && (
                <DisplayItem
                  label="Slutdato"
                  text={stringDateToPPP(kursushold.tilDato ?? "")}
                  isEditable={true}
                />
              )}
            </div>
            {editMode && (
              <div>
                <FormikTimePicker
                  config={{
                    component: "timepicker",
                    id: "tilKlDag",
                    label: "Sluttidspunkt",
                    initialValue: stringISODateToTime(
                      kursushold.tilDato ?? "",
                      kursushold.tilKlDag ?? "",
                    ),
                  }}
                  formik={formik}
                />
              </div>
            )}
            {!editMode && (
              <div>
                <DisplayItem
                  label="Sluttidspunkt"
                  text={stringISODateToTime(
                    kursushold.tilDato ?? "",
                    kursushold.tilKlDag ?? "",
                  )}
                  isEditable={true}
                />
              </div>
            )}
            {editMode && kursusTypeIsFMUProeve && (
              <div>
                <FormikDatePicker
                  config={{
                    component: "datepicker",
                    id: "censordato",
                    label: "Dato for censormøde",
                    initialValue: kursushold.censordato ?? undefined,
                  }}
                  formik={formik}
                />
              </div>
            )}
            {!editMode && kursusTypeIsFMUProeve && (
              <div>
                <DisplayItem
                  label="Dato for censormøde"
                  text={
                    kursushold.censordato
                      ? stringDateToPPP(kursushold.censordato)
                      : "-"
                  }
                  isEditable={true}
                />
              </div>
            )}
            <div>
              <DisplayItem
                label="Kursustype"
                text={kursushold?.kursustypeNavigation?.navn ?? ""}
                isEditable={false}
              />
            </div>{" "}
            {editMode && kursusTypeIsTemaOrFMUProeve && (
              <div>
                <TextInputFormik
                  formik={formik}
                  config={{
                    component: "textinput",
                    id: "pris",
                    initialValue: initialValues.pris?.toString() ?? "",
                    label: "Pris",
                  }}
                />
              </div>
            )}
            {!(editMode && kursusTypeIsTemaOrFMUProeve) && (
              <div>
                <DisplayItem
                  label="Pris"
                  text={defaultIfNullish(kursushold.pris)}
                  isEditable={false}
                />
              </div>
            )}
          </div>
        </div>
        {/* næste column */}
        <div className="col-12 col-xl-6 d-flex flex-column gap-4">
          <div className="c-item-list c-card">
            <h4 className="flex-100 mt-n1">Hotel / Sted</h4>
            {editMode && (
              <div>
                <label className="form-label">Hotel / Sted</label>
                <AutoCompleteFormControl
                  onChange={(e) => {
                    const selectedHotel = getHotelById(e);
                    if (selectedHotel) {
                      handleFormikChange(formik, "hotelId", selectedHotel.id);
                    } else {
                      handleFormikChange(formik, "hotelId", "");
                    }
                  }}
                  queryValues={{
                    data: hotels
                      ? hotels.map((h) => {
                          return {
                            ...h,
                            navn: h.navn1,
                          };
                        })
                      : undefined,
                    error: getHotelsError,
                    isPending: getHotelsLoading,
                  }}
                  value={values.hotelId ?? undefined}
                />
              </div>
            )}
            {!editMode && (
              <div>
                <DisplayItem
                  label="Hotel / Sted"
                  text={defaultIfNullish(hotel?.navn1)}
                  isEditable={true}
                />
              </div>
            )}
            <div>
              <DisplayItem
                label="Kontaktperson"
                text={hotel?.attPerson ?? "-"}
              />
            </div>
            <div>
              <DisplayItem label="Tlf." text={hotel?.tlfNr ?? "-"} />
            </div>
            <div>
              <DisplayItem label="E-mail" text={hotel?.eMail ?? "-"} />
            </div>
          </div>
          <div className="c-card">
            <div>
              {editMode && (
                <FormikTextArea
                  config={{
                    id: "bemaerkninger",
                    component: "textarea",
                    label: "Bemærkninger",
                    initialValue: kursushold.bemaerkninger ?? "",
                    placeholder: "",
                  }}
                  formik={formik}
                />
              )}
              {!editMode && (
                <>
                  <h5 className="form-label">Bemærkninger</h5>
                  <div
                    className="form-control--value is-editable"
                    style={{ whiteSpace: "pre-wrap", minHeight: "4.4rem" }}
                  >
                    {defaultIfNullish(kursushold.bemaerkninger)}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {editMode && (
          <>
            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <Button
                color="secondary"
                type="button"
                className="me-md-2"
                onClick={() => {
                  setEditMode(!editMode);
                  formik.resetForm();
                }}
              >
                Annuller
              </Button>
              <Button
                color="primary"
                type="submit"
                onClick={handleSubmit}
                disabled={!dirty || mutationIsLoading}
              >
                Gem
              </Button>
            </div>{" "}
            {mutationError && (
              <div className="d-flex justify-content-end">
                <small className={`text-danger mt-1 fw-semibold`}>
                  {" "}
                  Noget gik galt. Fejlkode {mutationError.status}
                </small>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Kursusoplysninger;
